import { AnimateOnScroll } from "@/components/AnimateOnScroll";
import { CheckCircle, LucideIcon } from "lucide-react";

interface Technology {
  name: string;
  description?: string;
  tools: string[];
}

interface TechnologiesGridProps {
  title: string;
  subtitle?: string;
  badge: {
    icon: LucideIcon;
    text: string;
    color: string;
  };
  technologies: Technology[];
  checkColor?: string; // e.g., "green" for text-green-500
}

export const TechnologiesGrid = ({ 
  title, 
  subtitle, 
  badge, 
  technologies,
  checkColor = "green"
}: TechnologiesGridProps) => {
  const BadgeIcon = badge.icon;

  return (
    <section className="py-12 md:py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6">
        <AnimateOnScroll animation="fade-up">
          <div className="text-center mb-10 md:mb-16">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-${badge.color}-100 dark:bg-${badge.color}-900/30 mb-3 md:mb-4`}>
              <BadgeIcon className={`w-3.5 h-3.5 md:w-4 md:h-4 text-${badge.color}-600`} />
              <span className={`text-${badge.color}-700 dark:text-${badge.color}-400 text-xs md:text-sm font-medium`}>{badge.text}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4 px-2">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-2">
                {subtitle}
              </p>
            )}
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {technologies.map((tech, index) => (
            <AnimateOnScroll key={index} animation="fade-up" delay={index * 100}>
              <div className="bg-card rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 shadow-md md:shadow-lg border border-border h-full flex flex-col">
                <h3 className="font-bold text-foreground text-sm sm:text-base mb-1.5 md:mb-2">{tech.name}</h3>
                {tech.description && (
                  <p className="text-muted-foreground text-xs sm:text-sm mb-3 md:mb-4 hidden sm:block">{tech.description}</p>
                )}
                <div className="space-y-1.5 sm:space-y-2">
                  {tech.tools.map((tool, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 sm:gap-2 py-1.5 sm:py-2 px-2 sm:px-3 bg-muted/50 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium text-foreground">
                      <CheckCircle className={`w-3 h-3 sm:w-4 sm:h-4 text-${checkColor}-500 flex-shrink-0`} />
                      <span className="truncate">{tool}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
};
